/**
 * TRABAJO QUE ESPERA A QUE HAYA ALGO EN PANTALLA.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ 🔴 `requestIdleCallback` NO SIGNIFICA «ya se pintó». SIGNIFICA «el hilo   ║
 * ║    principal está libre», Y ESO OCURRE ANTES DEL PRIMER PINTADO.         ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 *
 * No es teoría: se midió contra producción. Con el arranque de App Check movido
 * a `requestIdleCallback`, sus 346 KB seguían bajando ANTES del primer pintado
 * en las 12 corridas — en red rápida incluso ARRANCABAN ANTES que sin diferir
 * (303-444 ms contra 433-617). La idea era buena; la señal era la equivocada.
 *
 * Aquí la señal es el evento que de verdad dice «hay algo en pantalla»:
 * `first-contentful-paint` de la API de rendimiento. Y después de él se cede el
 * hilo hasta el primer hueco, para no robarle el fotograma siguiente a la app.
 *
 * ─── LAS TRES SALIDAS, PORQUE NINGUNA BASTA SOLA ────────────────────────────
 *
 *  1. YA PINTÓ ANTES DE PREGUNTAR. Se mira el búfer (`getEntriesByType('paint')`
 *     y `buffered: true`): quien llegue tarde no se queda esperando un evento
 *     que ya pasó. Es el caso normal a partir de la segunda llamada.
 *  2. SIN `PerformanceObserver` (o si observar falla). Se cae a `load`, que es
 *     más tardío pero seguro. Si el documento ya está completo, se sigue.
 *  3. 🔴 EL TOPE, Y NO ES ADORNO. Una pestaña en segundo plano puede no pintar
 *     NUNCA, y `load` puede no llegar si un recurso se cuelga. Sin tope, «se
 *     hace luego» se convierte en «no se hace jamás» — que en el caso de App
 *     Check sería quedarse sin las métricas con las que se decide el
 *     enforcement, o sea cambiar un problema por otro peor. El tope garantiza
 *     que la tarea ocurre, pintada o no.
 *
 * La tarea corre UNA sola vez: la primera salida que llegue desactiva las otras.
 *
 * ⚠️ LO QUE ESTO **NO** ES: una señal de «la red está libre». Después del primer
 * pintado las peticiones de datos del usuario pueden seguir en vuelo. Esto
 * garantiza que el trabajo diferido no compite con el PRIMER PÍXEL; para no
 * competir tampoco con los datos habría que esperar a `load` (ver `esperar`).
 */

/**
 * Tope por defecto. Diez segundos: más que cualquier primer pintado razonable
 * en 4G lenta (lo medido está muy por debajo) y lo bastante corto para que una
 * pestaña que nunca pinta no deje el trabajo sin hacer.
 */
export const TOPE_MS = 10000;

/** ¿Ya ocurrió el primer pintado con contenido? */
function yaPinto() {
  try {
    if (typeof performance === 'undefined' || typeof performance.getEntriesByType !== 'function') return false;
    return performance.getEntriesByType('paint').some((e) => e.name === 'first-contentful-paint');
  } catch (_) {
    return false;
  }
}

/**
 * Ejecuta `tarea` cuando ya hay algo en pantalla (o al vencer el tope).
 *
 * @param {Function} tarea
 * @param {{ topeMs?: number, esperar?: 'pintado' | 'carga' }} opciones
 *   `esperar: 'carga'` usa `load` en vez del primer pintado: más tarde, ya sin
 *   recursos críticos por bajar. Es la opción para trabajo pesado al que no le
 *   corre prisa; cuesta que todo lo que dependa de él ocurra mucho después.
 */
export function trasPrimerPintado(tarea, { topeMs = TOPE_MS, esperar = 'pintado' } = {}) {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    tarea();
    return;
  }

  let hecho = false;
  let observador = null;
  const temporizador = setTimeout(() => correr(), topeMs);

  function correr() {
    if (hecho) return;
    hecho = true;
    clearTimeout(temporizador);
    if (observador) { try { observador.disconnect(); } catch (_) {} observador = null; }
    window.removeEventListener('load', trasPintar);
    tarea();
  }

  /**
   * Ya hay algo en pantalla: se cede el hilo hasta el primer hueco para no
   * quedarnos con el fotograma siguiente. El `timeout` corto evita que «el
   * primer hueco» se vuelva indefinido en una pestaña ocupada.
   */
  function trasPintar() {
    if (hecho) return;
    if (window.requestIdleCallback) window.requestIdleCallback(() => correr(), { timeout: 1000 });
    else setTimeout(() => correr(), 0);
  }

  const porCarga = () => {
    if (document.readyState === 'complete') trasPintar();
    else window.addEventListener('load', trasPintar, { once: true });
  };

  if (esperar === 'carga') { porCarga(); return; }

  if (yaPinto()) { trasPintar(); return; }

  try {
    if (typeof PerformanceObserver === 'function') {
      observador = new PerformanceObserver((lista) => {
        const entradas = typeof lista.getEntries === 'function' ? lista.getEntries() : [];
        // Solo `first-contentful-paint`. `first-paint` puede ser un fondo pintado
        // sin una letra: dar eso por «ya se ve algo» es volver al mismo error de
        // señal, con otro nombre.
        if (entradas.some((e) => e && e.name === 'first-contentful-paint')) trasPintar();
      });
      observador.observe({ type: 'paint', buffered: true });
      return;
    }
  } catch (_) {
    observador = null;                    // observar falló: queda el respaldo
  }

  porCarga();
}
